import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    MenuItem,
    Grid,
    Pagination,
    IconButton,
    Tooltip,
} from '@mui/material';
import { Refresh, FilterList } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const ActivityLogs = () => {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        action: '',
        resource: '',
        startDate: '',
        endDate: '',
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
    });

    useEffect(() => {
        fetchLogs();
        fetchStats();
    }, [filters, pagination.page]);

    const fetchLogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/activity-logs`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    ...filters,
                    page: pagination.page,
                    limit: pagination.limit,
                },
            });

            setLogs(data.logs);
            setPagination(prev => ({
                ...prev,
                total: data.pagination.total,
                pages: data.pagination.pages,
            }));
            setLoading(false);
        } catch (error) {
            console.error('Error fetching logs:', error);
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/activity-logs/stats`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { days: 30 },
            });
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const getActionColor = (action) => {
        if (action.includes('created') || action.includes('login')) return 'success';
        if (action.includes('deleted') || action.includes('failed')) return 'error';
        if (action.includes('updated')) return 'info';
        return 'default';
    };

    const getActionLabel = (action) => {
        const labels = {
            login: 'تسجيل دخول',
            logout: 'تسجيل خروج',
            register: 'تسجيل جديد',
            campaign_created: 'إنشاء حملة',
            campaign_updated: 'تحديث حملة',
            campaign_deleted: 'حذف حملة',
            post_scheduled: 'جدولة منشور',
            post_sent: 'إرسال منشور',
            draft_created: 'إنشاء مسودة',
            draft_updated: 'تحديث مسودة',
            template_created: 'إنشاء قالب',
            template_used: 'استخدام قالب',
            ai_content_generated: 'توليد محتوى AI',
            subscription_upgraded: 'ترقية اشتراك',
        };
        return labels[action] || action;
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('ar-EG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">
                    سجل النشاطات
                </Typography>
                <Tooltip title="تحديث">
                    <IconButton onClick={fetchLogs}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Stats Cards */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    إجمالي النشاطات
                                </Typography>
                                <Typography variant="h3" fontWeight="bold">
                                    {stats.total}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    آخر 30 يوم
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    أكثر نشاط
                                </Typography>
                                <Typography variant="h4" fontWeight="bold">
                                    {Object.entries(stats.byAction).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {Object.entries(stats.byAction).sort((a, b) => b[1] - a[1])[0]?.[1] || 0} مرة
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                select
                                label="نوع النشاط"
                                value={filters.action}
                                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                            >
                                <MenuItem value="">الكل</MenuItem>
                                <MenuItem value="login">تسجيل دخول</MenuItem>
                                <MenuItem value="campaign_created">إنشاء حملة</MenuItem>
                                <MenuItem value="post_scheduled">جدولة منشور</MenuItem>
                                <MenuItem value="draft_created">إنشاء مسودة</MenuItem>
                                <MenuItem value="template_created">إنشاء قالب</MenuItem>
                                <MenuItem value="ai_content_generated">توليد AI</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="date"
                                label="من تاريخ"
                                value={filters.startDate}
                                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="date"
                                label="إلى تاريخ"
                                value={filters.endDate}
                                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Logs Table */}
            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>التاريخ</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>النشاط</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>المورد</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الحالة</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>IP</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                            لا توجد نشاطات
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                logs.map((log) => (
                                    <TableRow key={log._id} hover>
                                        <TableCell>{formatDate(log.createdAt)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getActionLabel(log.action)}
                                                size="small"
                                                color={getActionColor(log.action)}
                                            />
                                        </TableCell>
                                        <TableCell>{log.resource || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={log.status}
                                                size="small"
                                                color={log.status === 'success' ? 'success' : 'error'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                {log.ipAddress || '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Pagination
                            count={pagination.pages}
                            page={pagination.page}
                            onChange={(e, page) => setPagination({ ...pagination, page })}
                            color="primary"
                        />
                    </Box>
                )}
            </Card>
        </Container>
    );
};

export default ActivityLogs;
